import { cleanDescription } from '@/utils/common';
import { useCartStore } from '@/stores/cart';

/**
 * Generate Product schema for ProductPage
 * @param {Object} product - Shopify product data
 * @param {Object} entry - CMS entry data
 * @param {Object} siteIdentity - Site identity from app store
 * @returns {Object|null} Generated schema or null if no product data
 */
export const generateSchema = (product, entry, siteIdentity = {}) => {
  if (!product) {
    return null;
  }

  const { $localeRootPath, $getCurrentLocaleConfig } = useNuxtApp();
  const cartStore = useCartStore();
  const baseUrl = siteIdentity.url || 'https://blackroll.com';
  const productUrl = `${baseUrl}${$localeRootPath('/products/' + entry.shopifyHandle)}`;

  const localeConfig = $getCurrentLocaleConfig();
  const currency = localeConfig.currency;
  const country = localeConfig.language.split('-')[1];

  // Helper function to generate shipping details
  const generateShippingDetails = (variantPrice) => {
    const defaultShippingCost = '4.50';
    let shippingCost = defaultShippingCost;

    if (cartStore.freeShippingThreshold) {
      const productPrice = parseFloat(variantPrice);
      const threshold = parseFloat(cartStore.freeShippingThreshold);
      shippingCost = productPrice >= threshold ? '0' : defaultShippingCost;
    }

    return {
      '@type': 'OfferShippingDetails',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: shippingCost,
        currency: currency,
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: country,
      },
    };
  };

  const description = cleanDescription(product.descriptionHtml) || undefined;
  const fallbackImage = product.images?.edges?.[0]?.node?.src;

  const merchantReturnPolicy = {
    '@type': 'MerchantReturnPolicy',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 30,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/FreeReturn',
    applicableCountry: country,
  };

  // Generate images from Shopify product
  const generateImages = () => {
    if (!product.images?.edges) return null;
    return product.images.edges.map((edge) => edge.node.src);
  };

  // Generate offers from Shopify product variants
  const generateOffers = () => {
    if (!product.variants?.edges) return null;

    return product.variants.edges.map((edge) => {
      const variant = edge.node;
      const variantImage = variant.image?.src || fallbackImage;
      const offer = {
        '@type': 'Offer',
        price: variant.price.amount,
        priceCurrency: currency,
        availability: variant.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: productUrl,
        seller: {
          '@type': 'Organization',
          name: siteIdentity.name || 'BLACKROLL',
        },
        itemCondition: 'https://schema.org/NewCondition',
        itemOffered: {
          '@type': 'Product',
          '@id': `${productUrl}#variant-${variant.id}`,
          name: `${product.title} - ${variant.title}`,
          sku: variant.sku,
          ...(variantImage && { image: variantImage }),
          description,
        },
        shippingDetails: generateShippingDetails(variant.price.amount),
        hasMerchantReturnPolicy: merchantReturnPolicy,
      };

      return offer;
    });
  };

  // Generate additional properties for variants
  const generateAdditionalProperties = () => {
    if (!product.variants?.edges) return null;

    const properties = [];

    // Add variant options as properties
    product.variants.edges.forEach((edge) => {
      const variant = edge.node;

      // Add SKU as a property
      if (variant.sku) {
        properties.push({
          '@type': 'PropertyValue',
          name: 'SKU',
          value: variant.sku,
        });
      }

      // Add variant title as a property (e.g., "Small", "Medium", "Large")
      if (variant.title && variant.title !== 'Default Title') {
        properties.push({
          '@type': 'PropertyValue',
          name: 'Variant',
          value: variant.title,
        });
      }
    });

    return properties.length > 0 ? properties : null;
  };

  // Generate variants as separate products
  // Yotpo injects its own AggregateRating schema for products with reviews,
  // so we always emit variant offers (Google requires either offers or rating).
  const generateVariants = () => {
    if (!product.variants?.edges) return null;

    return product.variants.edges.map((edge) => {
      const variant = edge.node;
      const variantImage = variant.image?.src || fallbackImage;

      return {
        '@type': 'Product',
        '@id': `${productUrl}#variant-${variant.id}`,
        name: `${product.title} - ${variant.title}`,
        sku: variant.sku,
        isVariantOf: {
          '@type': 'ProductGroup',
          '@id': `${productUrl}#product-group`,
          productGroupID: product.id,
        },
        url: productUrl,
        ...(variantImage && { image: variantImage }),
        description,
        brand: {
          '@type': 'Brand',
          name: siteIdentity.siteName || 'BLACKROLL',
        },
        offers: {
          '@type': 'Offer',
          price: variant.price.amount,
          priceCurrency: currency,
          availability: variant.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          shippingDetails: generateShippingDetails(variant.price.amount),
          hasMerchantReturnPolicy: merchantReturnPolicy,
        },
      };
    });
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    '@id': `${productUrl}#product-group`,
    name: product.title,
    description,
    url: productUrl,
    productGroupID: product.id,
    brand: {
      '@type': 'Brand',
      name: siteIdentity.siteName || 'BLACKROLL',
    },
  };

  // Add images
  const images = generateImages();
  if (images) {
    schema.image = images;
  }

  // Add variants
  const variants = generateVariants();
  if (variants) {
    schema.hasVariant = variants;
  }

  // Add offers at ProductGroup level (always useful for rich snippets)
  const offers = generateOffers();
  if (offers) {
    schema.offers = offers;
  }

  // Add additional properties
  if (product.productType) {
    schema.category = product.productType;
  }

  // Add SKU from first variant
  if (product.variants?.edges?.[0]?.node?.sku) {
    schema.sku = product.variants.edges[0].node.sku;
  }

  // Add additional properties for variants
  const additionalProperties = generateAdditionalProperties();
  if (additionalProperties) {
    schema.additionalProperty = additionalProperties;
  }

  // Add weight and dimensions if available
  if (product.variants?.edges?.[0]?.node?.weight) {
    schema.weight = {
      '@type': 'QuantitativeValue',
      value: product.variants.edges[0].node.weight,
      unitText: 'kg',
    };
  }

  // Add merchant return policy
  schema.hasMerchantReturnPolicy = merchantReturnPolicy;

  return schema;
};
